import { LoadingButton, type LoadingButtonProps } from '@mui/lab'
import {
  type ButtonProps,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  type DialogTitleProps
} from '@mui/material'
import React from 'react'

import { usePopoverState } from 'hooks/use-popover-state'

type SetModalState = (nextState?: boolean) => void

export type ButtonWithConfirmationOnConfirm = (options: {
  setIsModalOpen: SetModalState
}) => unknown

type BaseButtonProps = {
  buttonCancelText?: string
  buttonConfirmText: string
  dialogBodyContent: string | React.ReactElement
  dialogTitleColor?: DialogTitleProps['color']
  dialogTitleContent: string | React.ReactElement
  onConfirm: ButtonWithConfirmationOnConfirm
  renderButton?: (props: {
    setIsModalOpen: SetModalState
  }) => React.ReactElement
}

type ButtonWithConfirmationProps = BaseButtonProps &
  Omit<ButtonProps, 'onClick'> & {
    buttonConfirmProps?: Omit<LoadingButtonProps, 'onClick'>
  }

export const ButtonWithConfirmation: React.FC<ButtonWithConfirmationProps> = ({
  buttonCancelText = 'Cancel',
  buttonConfirmProps,
  buttonConfirmText,
  children,
  dialogBodyContent,
  dialogTitleContent,
  dialogTitleColor,
  onConfirm,
  renderButton,
  ...rest
}) => {
  const drawerState = usePopoverState()

  const setIsModalOpen: SetModalState = (nextState?: boolean) => {
    if (nextState) {
      drawerState.open()
    } else {
      drawerState.close()
    }
  }

  const handleConfirmClick = async () => {
    await onConfirm({ setIsModalOpen })
  }

  return (
    <>
      {renderButton ? (
        renderButton({ setIsModalOpen })
      ) : (
        <Button variant="contained" {...rest} onClick={drawerState.open}>
          {children}
        </Button>
      )}
      <Dialog aria-labelledby="alert-dialog-title" open={drawerState.isOpen}>
        <DialogTitle color={dialogTitleColor} id="alert-dialog-title">
          {dialogTitleContent}
        </DialogTitle>
        <DialogContent>
          {typeof dialogBodyContent === 'string' ? (
            <DialogContentText>{dialogBodyContent}</DialogContentText>
          ) : (
            dialogBodyContent
          )}
        </DialogContent>
        <DialogActions>
          <Button
            data-testid={`dialog-button-cancel-${buttonCancelText}`}
            onClick={drawerState.close}
          >
            {buttonCancelText}
          </Button>
          <LoadingButton
            autoFocus
            data-testid={`dialog-button-confirm-${buttonConfirmText}`}
            variant="contained"
            onClick={handleConfirmClick}
            {...buttonConfirmProps}
          >
            {buttonConfirmText}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  )
}
